import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { useDispatch } from '@tmapy/redux';
import { useMessage } from '@tmapy/intl';
import {
  BtnBar,
  CustomSelect,
  DangerAlert,
  Dialog,
  FormItem,
  FormRow,
  InfoAlert,
  Input,
  SecondaryLink,
  Textarea,
  WaitingAlert,
  CloseDialogBtn,
} from '@tmapy/style-guide';

import type { NestedGeodesistResponse, UserListResponse } from '../../openapi/types';
import {
  DocsRetrieveResponse,
  useDocsRetrieveResource,
} from '../../openapi/operations/docsRetrieve';
import { geodesistsList } from '../../openapi/operations/geodesistsList';
import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { LogList } from '../../logs/components/LogList';
import { HistoryList } from '../../history/components/HistoryList';

import type { InputProps } from '../../hooks/useInput';
import {
  SSE_TYPE,
  useAuth,
  useLogDownloadLink,
  useFormItemServerErrors,
  useInput,
  useSelect,
  useSelector,
  useSSE,
} from '../../hooks';
import { QUERY_STATE } from '../../constants';

import { DOC_STATE, DOCDETAIL_LOAD_SUCCESS, DOCDETAIL_LOAD_WAITING } from '../constants';

import { docFormMessages, docTitleMessages } from './docsMessages';
import { docEditButtonsMsg, docEditMessages } from './docEditMessages';

import { defaultValues } from './defaultValues';
import { DocAttachments } from './DocAttachments';
import { DocBadge } from './DocBadge';
import { CheckRecordList } from './CheckRecordList';

import './Doc.scss';

const renderUserSelectItem = (item: UserListResponse | NestedGeodesistResponse) => (
  <>
    {item.name} {item.surname}
  </>
);

export type DocInitialImportEditContainerProps = {
  docId?: number;
};

export const DocInitialImport: React.FC<DocInitialImportEditContainerProps> = ({ docId }) => {
  useSSE(SSE_TYPE.DOCS, docId);

  const formatMessage = useMessage();
  const dispatch = useDispatch();

  const [isLogDialogShown, showLogDialog] = useState(false);
  const [checkLogId, setCheckLogId] = useState<number | undefined>(undefined);

  const attachments = useSelector((state) => state.doc.data.attachments);
  const serverErrors = useSelector((state) => state.docedit.errors);
  const formItemServerErrors = useFormItemServerErrors(serverErrors);

  const auth = useAuth();

  const resource = useDocsRetrieveResource(docId ? { id: docId } : null);

  const data = useMemo(() => {
    return resource.data
      ? { ...resource.data, attachments }
      : { ...defaultValues, state: DOC_STATE.INPROGRESS };
  }, [resource.data, attachments]);

  const acl = useMemo(
    () => ({
      /**
       * update a partial_update je nutno mit nastaveno stejne (FE,BE), ukladani vzdy jde pres PATCH tedy PartialUpdate
       * partial_update urcuje form disable jednotlivych polozek
       * update urcuje viditelnost ukladaciho tlacitka
       */
      canUpdate: auth.hasPermission('InitialDocs', 'partial_update', data),
      docsUpdate: auth.hasPermission('InitialDocs', 'update', data),

      docsActionShowLog: auth.hasPermission('InitialDocs', 'action.logs', data),
      docsActionChecksVisibility: auth.hasPermission('InitialDocs', 'checks_visibility', data),

      docsDestroy: auth.hasPermission('InitialDocs', 'destroy', data),
      docsActionImport: auth.hasPermission('InitialDocs', 'action.import_doc', data),
      docsActionImportInterrupt: auth.hasPermission('InitialDocs', 'action.import_interrupt', data),
      docsActionClose: auth.hasPermission('InitialDocs', 'action.close', data),
      docsActionCloseInterrupt: auth.hasPermission('InitialDocs', 'action.close_interrupt', data),
      docsActionCancel: auth.hasPermission('InitialDocs', 'action.cancel', data),
    }),
    [auth, data],
  );

  const [showLogDownloadError, setShowLogDownloadError] = useState(false);
  const handleShowDownloadLogError = (visible: boolean) => {
    setShowLogDownloadError(visible);
  };

  const logDownloadLink = useLogDownloadLink(
    resource.data?.url,
    data.flow,
    data.state === DOC_STATE.CLOSE_FAILED ? 'download_close_log' : 'download_import_log',
    handleShowDownloadLogError,
  );

  useEffect(() => {
    if (!resource.data) {
      dispatch({
        type: DOCDETAIL_LOAD_WAITING,
      });
    } else {
      dispatch({
        type: DOCDETAIL_LOAD_SUCCESS,
        data: resource.data,
      });
    }
  }, [resource.data, dispatch]);

  // ACTIONS
  const handleRefetch = useCallback(() => {
    resource.refetch();
  }, [resource]);

  const handleCheckClick = useCallback((id: number) => {
    setCheckLogId(id);
    showLogDialog(true);
  }, []);

  // HOOK FOR INPUTS
  const name = useInput<InputProps>({
    name: 'name',
    isDisabled: true,
    initialValue: data.name,
    label: formatMessage(docFormMessages.name),
  });

  const description = useInput<InputProps>({
    name: 'description',
    isDisabled: true,
    initialValue: data.description,
    label: formatMessage(docFormMessages.description),
  });

  const prepareGeodesistItems = useCallback(async (search: string) => {
    const response = await geodesistsList({ query: { search } });
    if (response.ok) {
      return response.data?.results ?? null;
    }
    return null;
  }, []);
  const geodesist = useSelect<DocsRetrieveResponse['geodesist'], DocsRetrieveResponse['geodesist']>(
    {
      name: 'geodesist',
      isDisabled: true,
      label: formatMessage(docFormMessages.geodesist),
      initialValue: data.geodesist,
      prepareItems: prepareGeodesistItems,
      renderValue: (item) => (item.name || item.surname ? `${item.name} ${item.surname}` : ''),
      renderItem: renderUserSelectItem,
    },
  );

  const building_id = useInput<InputProps>({
    name: 'building_id',
    isDisabled: true,
    initialValue: data.building_id,
    label: formatMessage(docFormMessages.building_id),
  });

  const owner_name = useInput<InputProps>({
    name: 'owner_name',
    isDisabled: true,
    initialValue: data.owner_name,
    label: formatMessage(docFormMessages.owner_name),
  });

  const processor_name = useInput<InputProps>({
    name: 'processor_name',
    isDisabled: true,
    initialValue: data.processor_name,
    label: formatMessage(docFormMessages.processor_name),
  });

  const processor_organization = useInput<InputProps>({
    name: 'processor_organization',
    isDisabled: true,
    initialValue: data.processor_organization,
    label: formatMessage(docFormMessages.processor_organization),
  });

  // DATE
  const geodeticallySurveyedDate = useMemo(
    () => data.geodetically_surveyed,
    [data.geodetically_surveyed],
  );
  const geodetically_surveyed = useInput<InputProps, string>({
    name: 'geodetically_surveyed',
    isDisabled: true,
    initialValue: geodeticallySurveyedDate,
    label: formatMessage(docFormMessages.geodetically_surveyed),
  });

  // DATE
  const processedDate = useMemo(() => data.processed, [data.processed]);
  const processed = useInput<InputProps, string>({
    name: 'processed',
    isDisabled: true,
    initialValue: processedDate,
    label: formatMessage(docFormMessages.processed),
  });

  // DATE
  const verifiedDate = useMemo(() => data.verified, [data.verified]);
  const verified = useInput<InputProps, string>({
    name: 'verified',
    isDisabled: true,
    initialValue: verifiedDate,
    label: formatMessage(docFormMessages.verified),
  });

  const verification_number = useInput<InputProps>({
    name: 'verification_number',
    isDisabled: true,
    initialValue: data.verification_number,
    label: formatMessage(docFormMessages.verification_number),
  });

  // ATTACHMENTS
  const uploadHelpText = formatMessage.fallback(['document.initialImport.uploadHelpText']);

  // LOG
  const handleCancelLogDialogBtnClick = useCallback(() => {
    setCheckLogId(undefined);
    showLogDialog(false);
  }, []);

  return (
    <MainContent width='full' key={`${resource.data?.id}`}>
      {(resource.queryState === QUERY_STATE.SUCCESS || !docId) && (
        <article>
          <div className='dmx-doc--head sg-u-vs-2'>
            <div className='sg-a-d-f sg-a-g-2 sg-a-ai-c sg-a-jc-sb sg-a-fw-w'>
              <header className='sg-a-d-f sg-a-ai-c sg-a-g-1 sg-a-fw-w'>
                <PageTitle>
                  {formatMessage.lookup(docTitleMessages, 'initialImport', {
                    id: data.id || 'empty',
                  })}
                </PageTitle>
                {data.state && (
                  <DocBadge
                    docState={data.state}
                    flow={data.flow}
                    rejectedBySubjekty={data.rejected_by_subjekty}
                  />
                )}
              </header>
              <BtnBar>
                {!!docId && acl.docsActionShowLog && (
                  <SecondaryLink {...logDownloadLink} download>
                    <FormattedMessage {...docEditButtonsMsg.docBtnDownloadLog} />
                  </SecondaryLink>
                )}
              </BtnBar>
            </div>
            {showLogDownloadError && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.fileDownloadError} />
              </DangerAlert>
            )}
            {resource.queryState === QUERY_STATE.WAITING && (
              <WaitingAlert>
                <FormattedMessage {...docEditMessages.queryStateWaiting} />
              </WaitingAlert>
            )}
            {formItemServerErrors.non_field_errors && (
              <DangerAlert>{formItemServerErrors.non_field_errors.errorMessage!}</DangerAlert>
            )}
            {resource.queryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.loadQueryStateFail} />
              </DangerAlert>
            )}
          </div>
          <div className='dmx-doc--cols'>
            <div className='sg-u-vs-4'>
              <div className='sg-u-vs-4'>
                <section>
                  <h3 className='dmx-doc--attributesTitle sg-u-type-h2 sg-a-ml-2'>&nbsp;</h3>
                  <div className='sg-u-box sg-a-p-2 sg-u-vs-2 sg-a-mt-1'>
                    <FormRow>
                      <FormItem {...name}>
                        <Input {...name} />
                      </FormItem>
                    </FormRow>
                    <FormRow>
                      <FormItem {...description}>
                        <Textarea {...description} />
                      </FormItem>
                    </FormRow>
                    <FormRow>
                      <FormItem {...geodesist}>
                        <CustomSelect {...geodesist} />
                      </FormItem>
                    </FormRow>
                    <FormRow>
                      <FormItem {...building_id}>
                        <Input {...building_id} />
                      </FormItem>
                    </FormRow>
                    <FormRow>
                      <FormItem {...owner_name}>
                        <Input {...owner_name} />
                      </FormItem>
                    </FormRow>
                    <FormRow>
                      <FormItem {...processor_name}>
                        <Input {...processor_name} />
                      </FormItem>
                    </FormRow>
                    <FormRow>
                      <FormItem {...processor_organization}>
                        <Input {...processor_organization} />
                      </FormItem>
                    </FormRow>
                    <FormRow>
                      <FormItem {...geodetically_surveyed}>
                        <Input type='date' {...geodetically_surveyed} />
                      </FormItem>
                    </FormRow>
                    <FormRow>
                      <FormItem {...processed}>
                        <Input type='date' {...processed} />
                      </FormItem>
                    </FormRow>
                    <FormRow>
                      <FormItem {...verified}>
                        <Input type='date' {...verified} />
                      </FormItem>
                    </FormRow>
                    <FormRow>
                      <FormItem {...verification_number}>
                        <Input {...verification_number} />
                      </FormItem>
                    </FormRow>
                  </div>
                </section>
              </div>

              <section>
                <h3 className='sg-u-type-h2 sg-a-ml-2'>
                  <FormattedMessage {...docFormMessages.attachments} />
                </h3>
                <div className='sg-u-box sg-a-d-ib sg-a-mw-100% sg-a-p-2 sg-a-mt-1 sg-u-vs-2'>
                  {uploadHelpText && (
                    <div className='sg-a-mw-fc'>
                      <InfoAlert>{uploadHelpText}</InfoAlert>
                    </div>
                  )}
                  <DocAttachments
                    doc={resource?.data}
                    prejimkaType={data.prejimka_type}
                    attachments={attachments}
                  />
                </div>
              </section>
            </div>

            <div className='dmx-doc--rightCol sg-u-vs-4'>
              {resource?.data?.checks &&
                resource?.data?.checks.length > 0 &&
                acl.docsActionChecksVisibility && (
                  <section>
                    <h3 className='sg-u-type-h2 sg-a-ml-2'>
                      <FormattedMessage {...docFormMessages.checks} />
                    </h3>
                    <div className='sg-u-box sg-a-mt-1 dmx-a-mw-fc'>
                      <CheckRecordList
                        docId={docId}
                        items={resource?.data?.checks}
                        onClick={handleCheckClick}
                        onRefetch={handleRefetch}
                      />
                    </div>
                  </section>
                )}

              {acl.docsActionShowLog && (
                <section>
                  <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                    <FormattedMessage {...docFormMessages.logs} />
                  </h3>
                  <LogList objectId={docId!} resourceType={'doc'} />
                </section>
              )}

              <section>
                <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                  <FormattedMessage {...docFormMessages.history} />
                </h3>
                <HistoryList history={data.history} />
              </section>
            </div>
          </div>

          {isLogDialogShown && (
            <Dialog
              onEscKeyDown={handleCancelLogDialogBtnClick}
              onOverlayClick={handleCancelLogDialogBtnClick}
            >
              <CloseDialogBtn onClick={handleCancelLogDialogBtnClick} />
              <div className='sg-a-p-4 sg-u-vs-2'>
                <PageTitle>
                  <FormattedMessage {...docFormMessages.logs} />
                </PageTitle>
                <LogList objectId={checkLogId!} resourceType={'doc'} />
              </div>
            </Dialog>
          )}
        </article>
      )}
    </MainContent>
  );
};
